import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../SEO';

// Lazy-load heavy dependencies
const ChatBot = React.lazy(() => import('./ChatBot'));
const MotionDiv = React.lazy(() => import('framer-motion').then(mod => ({ default: mod.motion.div })));
const MotionH1 = React.lazy(() => import('framer-motion').then(mod => ({ default: mod.motion.h1 })));

// Define props interface for HeroSection
interface HeroSectionProps {
  language?: string;
  openChatBot?: () => void;
}

// Define translations for all 12 languages
const TRANSLATIONS: Record<string, {
  titlePart1: string;
  titlePart2: string;
  description: string;
  buttonContactUs: string;
  buttonChatBot: string;
  imageAlt: string;
}> = {
  "en": {
    titlePart1: "MADE IN GERMANY ©",
    titlePart2: "Premium Quality Worldwide",
    description: "At Made in Germany, we specialize in delivering premium German-engineered products to global markets through efficient logistics via cargo ships, trains, and trucks in secure containers. Our expertise ensures unparalleled quality, reliability, and timely delivery, connecting German craftsmanship with businesses worldwide.",
    buttonContactUs: "Contact Us",
    buttonChatBot: "Business Assistant",
    imageAlt: "German Precision Worldwide"
  },
  "de": {
    titlePart1: "MADE IN GERMANY ©",
    titlePart2: "Premiumqualität weltweit",
    description: "Bei Made in Germany sind wir darauf spezialisiert, erstklassige deutsche Ingenieursprodukte über effiziente Logistik mit Frachtschiffen, Zügen und LKW in sicheren Containern auf globale Märkte zu liefern. Unsere Expertise gewährleistet unübertroffene Qualität, Zuverlässigkeit und pünktliche Lieferung, die deutsche Handwerkskunst mit Unternehmen weltweit verbindet.",
    buttonContactUs: "Kontaktieren Sie uns",
    buttonChatBot: "Geschäftsassistent",
    imageAlt: "Deutsche Präzision weltweit"
  },
  "es": {
    titlePart1: "MADE IN GERMANY ©",
    titlePart2: "Calidad Premium Mundial",
    description: "En Made in Germany, nos especializamos en entregar productos premium de ingeniería alemana a mercados globales mediante una logística eficiente con barcos de carga, trenes y camiones en contenedores seguros. Nuestra experiencia garantiza calidad inigualable, confiabilidad y entrega puntual, conectando la artesanía alemana con empresas de todo el mundo.",
    buttonContactUs: "Contáctenos",
    buttonChatBot: "Asistente de Negocios",
    imageAlt: "Precisión Alemana Mundial"
  },
  "fr": {
    titlePart1: "MADE IN GERMANY ©",
    titlePart2: "Qualité Premium Mondiale",
    description: "Chez Made in Germany, nous excellons dans la livraison de produits haut de gamme conçus en Allemagne vers les marchés mondiaux grâce à une logistique efficace par cargos, trains et camions dans des conteneurs sécurisés. Notre expertise garantit une qualité inégalée, une fiabilité et des livraisons ponctuelles, reliant l’artisanat allemand aux entreprises du monde entier.",
    buttonContactUs: "Contactez-nous",
    buttonChatBot: "Assistant Commercial",
    imageAlt: "Précision Allemande Mondiale"
  },
  "it": {
    titlePart1: "MADE IN GERMANY ©",
    titlePart2: "Qualità Premium nel Mondo",
    description: "In Made in Germany, ci specializziamo nella consegna di prodotti premium di ingegneria tedesca ai mercati globali attraverso una logistica efficiente con navi cargo, treni e camion in container sicuri. La nostra competenza garantisce qualità senza pari, affidabilità e consegne puntuali, collegando l’artigianato tedesco con le imprese di tutto il mondo.",
    buttonContactUs: "Contattaci",
    buttonChatBot: "Assistente Aziendale",
    imageAlt: "Precisione Tedesca nel Mondo"
  },
  "nl": {
    titlePart1: "MADE IN GERMANY ©",
    titlePart2: "Premiumkwaliteit Wereldwijd",
    description: "Bij Made in Germany zijn we gespecialiseerd in het leveren van premium Duitse technische producten aan wereldwijde markten via efficiënte logistiek met vrachtschepen, treinen en vrachtwagens in veilige containers. Onze kundigheid verseker ongeëvenaarde kwaliteit, betrouwbaarheid en tijdige levering, en verbindt Duits vakmanschap met bedrijven wereldwijd.",
    buttonContactUs: "Neem Contact Op",
    buttonChatBot: "Bedrijfsassistent",
    imageAlt: "Duitse Precisie Wereldwijd"
  },
  "sa": {
    titlePart1: "صنع في ألمانيا ©",
    titlePart2: "الجودة الممتازة عالميًا",
    description: "في صنع في ألمانيا، نحن متخصصون في توصيل منتجات ألمانية هندسية فائقة الجودة إلى الأسواق العالمية من خلال لوجستيات فعالة عبر سفن الشحن والقطارات والشاحنات في حاويات آمنة. خبرتنا تضمن الجودة اللا مثيل لها والموثوقية والتسليم في الوقت المحدد، رابطة الحرفية الألمانية مع الشركات في جميع أنحاء العالم.",
    buttonContactUs: "اتصل بنا",
    buttonChatBot: "مساعد الأعمال",
    imageAlt: "الدقة الألمانية عالميًا"
  },
  "hk": {
    titlePart1: "德國製造 ©",
    titlePart2: "全球優質",
    description: "在德國製造，我們專注於通過高效物流，利用貨船、火車和卡車在安全容器中，將優質德國工程產品交付全球市場。我們的專業知識確保無與倫比的品質、可靠性和準時交付，將德國工藝與全球企業連接起來。",
    buttonContactUs: "聯繫我們",
    buttonChatBot: "商業助理",
    imageAlt: "德國精度全球"
  },
  "sg": {
    titlePart1: "德国制造 ©",
    titlePart2: "全球优质",
    description: "在德国制造，我们专注于通过高效物流，利用货船、火车和卡车在安全容器中，将优质德国工程产品交付全球市场。我们的专业知识确保无与伦比的品质、可靠性和准时交付，将德国工艺与全球企业连接起来。",
    buttonContactUs: "联系我们",
    buttonChatBot: "商业助理",
    imageAlt: "德国精度全球"
  },
  "za": {
    titlePart1: "MADE IN GERMANY ©",
    titlePart2: "Premium Kwaliteit Wêreldwyd",
    description: "By Made in Germany spesialiseer ons in die lewering van premium Duitse vervaardigde produkte aan globale markte deur doeltreffende logistiek met vrag skepe, treine en vragmotors in veilige houers. Ons kundigheid verseker ongeëwenaarde kwaliteit, betroubaarheid en tydige aflewering, wat Duitse vakmanskap met besighede wêreldwyd verbind.",
    buttonContactUs: "Kontak Ons",
    buttonChatBot: "Besigheidsassistent",
    imageAlt: "Duitse Presisie Wêreldwyd"
  },
  "kr": {
    titlePart1: "메이드 인 저머니 ©",
    titlePart2: "전 세계 프리미엄 품질",
    description: "메이드 인 저머니에서는 화물선, 기차, 트럭을 통해 안전한 컨테이너로 효율적인 물류를 통해 독일 엔지니어링의 프리미엄 제품을 전 세계 시장에 공급하는 데 전문화되어 있습니다. 우리의 전문성은 타의 추종을 불허하는 품질, 신뢰성 및 적시 배송을 보장하며, 독일 장인 정신을 전 세계 기업과 연결합니다.",
    buttonContactUs: "문의하기",
    buttonChatBot: "비즈니스 조수",
    imageAlt: "독일 정밀 전 세계"
  },
  "jp": {
    titlePart1: "メイド・イン・ジャーマニー ©",
    titlePart2: "世界的なプレミアム品質",
    description: "メイド・イン・ジャーマニーでは、貨物船、列車、トラックによる効率的な物流と安全なコンテナを通じて、ドイツのエンジニアリングによるプレミアム製品を世界市場に供給することに特化しています。私たちの専門知識は、他に類を見ない品質、信頼性、納期厳守を保証し、ドイツの職人技を世界中の企業と結びつけます。",
    buttonContactUs: "お問い合わせ",
    buttonChatBot: "ビジネスアシスタント",
    imageAlt: "ドイツの精密世界"
  }
};

// Image constants
const BACKGROUND_IMAGES = [
  '/hero/made-in-germany-cargo.webp',
  '/hero/made-in-germany-ship.webp',
  '/hero/made-in-germany-truck.webp',
  '/hero/made-in-germany-train.webp',
];
const HERO_IMAGE = '/made-in-germany-hero.webp';

const HeroSection: React.FC<HeroSectionProps> = React.memo(({ language = 'en', openChatBot }) => {
  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];
  const navigate = useNavigate();
  const [isChatBotOpen, setIsChatBotOpen] = useState(false);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detect mobile vs desktop on mount and resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Preload only the first background image for LCP
  useEffect(() => {
    const initialBgImage = BACKGROUND_IMAGES[0]; // Single set of images
    const bgImg = new Image();
    bgImg.src = initialBgImage;
    bgImg.onload = () => setIsBackgroundLoaded(true);
    bgImg.onerror = () => setIsBackgroundLoaded(true); // Handle errors gracefully
  }, []);

  // Background image slider
  useEffect(() => {
    if (isBackgroundLoaded) {
      const interval = setInterval(() => {
        setCurrentBgIndex((prev) => (prev + 1) % BACKGROUND_IMAGES.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isBackgroundLoaded]);

  const handleContactUsClick = () => navigate('/contactform');
  const handleChatBotClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openChatBot ? openChatBot() : setIsChatBotOpen(true);
  };

  return (
    <>
      <SEO
        title="Made in Germany – High-Quality German Products for Global Markets"
        description="Explore the best Made in Germany products. Connect with top German manufacturers and suppliers for premium quality goods."
        keywords="Made in Germany, German products, German manufacturers, premium quality, German engineering, B2B products"
        url="https://made-in-germany.uk"
        image="https://made-in-germany.uk/static/images/hero-image.webp"
      />

      {/* Preloader */}
      {!isBackgroundLoaded && (
        <div className="fixed inset-0 flex items-center justify-center bg-black z-50">
          <span className="text-white text-lg animate-pulse">Loading...</span>
        </div>
      )}

      <section
        className="relative min-h-[90vh] w-full overflow-hidden flex items-center justify-center bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out"
        style={{
          backgroundImage: isBackgroundLoaded ? `url(${BACKGROUND_IMAGES[currentBgIndex]})` : 'none',
          backgroundColor: '#000000', // Fallback for instant render
          minHeight: '90vh', // Explicit inline style for CLS
        }}
      >
        {/* Overlay for text visibility - Fixed position */}
        <div
          className="absolute inset-0 bg-black bg-opacity-50 z-0"
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />

        {/* Main Content Container - Reserved space */}
        <div
          className="relative z-10 container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 h-full flex flex-col justify-center py-8 sm:py-12 md:py-16 lg:py-20"
          style={{ minHeight: '500px' }} // Reserve space to prevent shift
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 xl:gap-[110px] items-center mt-[-2rem] sm:mt-[-3rem] md:mt-[-4rem]">
            {/* Left Content (Text renders immediately) */}
            <div className="lg:col-span-5 flex flex-col space-y-4 sm:space-y-6 w-full max-w-full pl-0 min-h-[300px]">
              <Suspense fallback={<div>Loading text...</div>}>
                <MotionDiv
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="p-[10px] rounded-[10px] transition-all duration-300 w-full lg:w-[160%] xl:w-[190%] max-w-[95vw] lg:max-w-[80vw] xl:max-w-[90vw] text-center lg:text-left"
                  style={{ minHeight: '250px' }} // Reserve space for content
                >
                  <MotionH1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-lg"
                  >
                    <span style={{ color: '#FFFFFF' }}>MADE </span>
                    <span style={{ color: '#D01020' }}>IN </span>
                    <span style={{ color: '#FFCE00' }}>GERMANY © </span>
                    <br />
                    <span className="text-white">{translations.titlePart2}</span>
                  </MotionH1>
                  {isBackgroundLoaded && (
                    <>
                      <p className="text-sm mt-5 sm:text-base lg:text-lg text-gray-300 max-w-2xl mx-auto sm:mx-0">
                        {translations.description}
                      </p>
                      <MotionDiv
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-6 mt-6 justify-center lg:justify-start"
                      >
                        <button
                          className="px-11 py-2 w-50 md:w-50 rounded-[5px] bg-[#FFFFFF] text-[#0B111F] font-medium transition-all duration-200 hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#FFFFFF]"
                          onClick={handleContactUsClick}
                        >
                          {translations.buttonContactUs}
                        </button>
                        <button
                          className="px-4 py-2 w-50 md:w-50 rounded-[5px] bg-transparent text-white font-medium border border-[#FFFFFF] transition-all duration-200 hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#0B111F]"
                          onClick={handleChatBotClick}
                        >
                          {translations.buttonChatBot}
                        </button>
                      </MotionDiv>
                    </>
                  )}
                </MotionDiv>
              </Suspense>
            </div>

            {/* Right Image (Loads after background) */}
            <div
              className="lg:col-span-7 flex justify-center lg:justify-end items-center w-full mt-4 lg:mt-0 relative min-h-[200px]"
              style={{ minHeight: isMobile ? '200px' : '480px' }} // Reserve space
            >
              {isBackgroundLoaded && (
                <img
                  src={HERO_IMAGE}
                  alt={translations.imageAlt}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width={isMobile ? 160 : 384} // Explicit dimensions for CLS
                  height={isMobile ? 200 : 480}
                  className="object-contain transform hover:scale-105 transition-transform duration-300 animate-float relative z-10"
                  style={{ filter: 'drop-shadow(0px 0px 10px rgba(11,17,31,0.8))' }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Inline Critical CSS */}
        <style jsx>{`
          @keyframes float {
            0% { transform: translateY(0); }
            50% { transform: translateY(-20px); }
            100% { transform: translateY(0); }
          }
          .animate-float {
            animation: float 4s infinite ease-in-out;
          }
          h1 {
            font-size: 2rem;
            font-weight: bold;
            color: #fff;
            line-height: 1.2;
            min-height: 80px; /* Reserve space for multi-line text */
          }
          .min-h-[90vh] {
            min-height: 90vh;
          }
          .min-h-[300px] {
            min-height: 300px;
          }
          .min-h-[200px] {
            min-height: 200px;
          }
          @media (min-width: 640px) {
            h1 { font-size: 2.5rem; min-height: 100px; }
          }
          @media (min-width: 768px) {
            h1 { font-size: 3rem; min-height: 120px; }
            .grid { display: grid; }
            .text-center { text-align: left; }
          }
          @media (min-width: 1024px) {
            h1 { font-size: 3.75rem; min-height: 150px; }
          }
          @media (max-width: 767px) {
            .container { padding: 1rem; }
            .grid { display: block; }
            .text-center { text-align: center; }
            .justify-center { justify-content: center; }
          }
        `}</style>
      </section>

      <Suspense fallback={<div>Loading ChatBot...</div>}>
        <ChatBot
          isOpen={isChatBotOpen}
          onClose={() => setIsChatBotOpen(false)}
          language={language}
        />
      </Suspense>
    </>
  );
});

// Preload critical images
export const PreloadImages = () => (
  <>
    <link rel="preload" as="image" href={BACKGROUND_IMAGES[0]} fetchPriority="high" />
    <link rel="preload" as="image" href={HERO_IMAGE} fetchPriority="high" />
  </>
);

export default HeroSection;